<template>
  <div class="min-h-screen bg-gray-50">

    <!-- Back nav -->
    <div class="bg-white border-b border-gray-100 sticky top-0 z-10">
      <div class="max-w-4xl mx-auto px-4 sm:px-6 py-3 tw-flex items-center justify-between gap-4">
        <router-link
          to="/profile/update#personal-home"
          class="tw-flex items-center gap-1.5 text-sm text-gray-500 no-underline hover:text-gray-800 transition-colors"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
          </svg>
          My Foodleap
        </router-link>
        <button
          @click="exportPDF"
          class="tw-flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-800 transition-colors bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-lg"
        >
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
          </svg>
          Export PDF
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="isLoading" class="max-w-4xl mx-auto px-4 sm:px-6 py-16 tw-flex justify-center">
      <svg class="animate-spin h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
      </svg>
    </div>

    <div v-else-if="!radarData" class="max-w-4xl mx-auto px-4 sm:px-6 py-20 text-center text-gray-400">
      No Radar found for this period.
    </div>

    <div v-else class="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">

      <!-- Header -->
      <div class="tw-flex items-end justify-between gap-4 flex-wrap">
        <h1 class="text-2xl font-bold text-gray-900 m-0">{{ radarData.title || 'Radar' }}</h1>
        <select
          v-if="radarQuarters.length > 0"
          class="no-print text-sm border border-gray-200 rounded-lg px-3 py-2 text-gray-700 bg-white focus:outline-none"
          :value="innovationRadarId"
          @change="$router.push('/innovation-radar/' + $event.target.value)"
          :aria-label="$t('trp_period_select')"
        >
          <option v-for="q in radarQuarters" :key="q.id" :value="String(q.id)">
            {{ q.title }}
          </option>
        </select>
        <p v-else class="text-xs font-semibold uppercase tracking-widest text-primary m-0">{{ quarterLabel }}</p>
      </div>

      <!-- Summary + Key Insights -->
      <div v-if="radarData.summary || radarData.keyInsights" class="gap-4">
        <div v-if="radarData.summary" class="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <p class="text-[11px] font-semibold uppercase tracking-widest text-gray-400 mb-2">Summary</p>
          <p class="text-sm text-gray-700 leading-relaxed m-0">{{ radarData.summary }}</p>
        </div>
        <div v-if="radarData.keyInsights" class="bg-white rounded-xl border border-gray-100 shadow-sm p-5 mt-4">
          <p class="text-[11px] font-semibold uppercase tracking-widest text-gray-400 mb-2">Key Insights</p>
          <p class="text-sm text-gray-700 leading-relaxed m-0" v-html="radarData.keyInsights"></p>
        </div>
      </div>

      <!-- Radar card -->
      <div class="bg-white rounded-xl border border-gray-100 shadow-sm p-5 md:p-6 space-y-5">

        <!-- Legend (horizontal pills) + reading guide -->
        <div class="tw-flex flex-col sm:flex-row sm:items-start gap-3 justify-between">

          <!-- Value chain pills -->
          <div class="tw-flex flex-row flex-wrap gap-1.5">
            <button
              v-for="cat in valueChainCategories"
              :key="cat.vc"
              @click="toggleCategory(cat.vc)"
              :class="[
                'tw-flex items-center gap-1.5 pl-2 pr-2.5 py-1 rounded-full border text-xs font-medium transition-all',
                activeCategories.includes(cat.vc)
                  ? 'border-transparent text-white shadow-sm'
                  : 'border-gray-200 text-gray-400 bg-white hover:border-gray-300',
              ]"
              :style="activeCategories.includes(cat.vc) ? { backgroundColor: cat.color, borderColor: cat.color } : {}"
            >
              <span class="w-1.5 h-1.5 rounded-full shrink-0" :style="{ backgroundColor: activeCategories.includes(cat.vc) ? 'rgba(255,255,255,0.7)' : cat.color }"/>
              {{ cat.vc }}
              <span :class="activeCategories.includes(cat.vc) ? 'opacity-70' : 'text-gray-300'">{{ cat.count }}</span>
            </button>
            <button
              v-if="activeCategories.length < valueChainCategories.length"
              class="pl-2 pr-2.5 py-1 rounded-full border border-dashed border-gray-300 text-xs text-gray-400 hover:text-gray-600 transition-colors"
              @click="activeCategories = valueChainCategories.map(c => c.vc)"
            >
              Show all
            </button>
          </div>

          <!-- Reading guide -->
          <div class="text-[11px] text-gray-400 leading-relaxed shrink-0 text-right hidden sm:block">
            <div><span class="font-semibold text-gray-500">X →</span> Scale readiness</div>
            <div><span class="font-semibold text-gray-500">Y ↑</span> Strategic impact</div>
          </div>
        </div>

        <!-- Radar chart — full width -->
        <div
          class="relative w-full rounded-full border border-gray-200"
          style="padding-top: 100%; background: radial-gradient(circle, #f8fafb 0%, #ffffff 70%)"
          ref="radarContainer"
        >
          <!-- Concentric circles -->
          <div class="absolute inset-0 tw-flex items-center justify-center pointer-events-none">
            <div class="absolute rounded-full border border-gray-200" style="width:75%;height:75%"/>
            <div class="absolute rounded-full border border-gray-200" style="width:50%;height:50%"/>
            <div class="absolute rounded-full border border-gray-200" style="width:25%;height:25%"/>
          </div>
          <!-- Axis lines -->
          <div class="absolute inset-0 pointer-events-none">
            <div class="absolute bg-gray-200" style="top:50%;left:0;right:0;height:1px"/>
            <div class="absolute bg-gray-200" style="left:50%;top:0;bottom:0;width:1px"/>
          </div>
          <!-- Axis labels -->
          <span class="absolute text-[11px] text-gray-400 pointer-events-none font-medium" style="top:50%;left:10px;transform:translateY(-50%)">Early</span>
          <span class="absolute text-[11px] text-gray-400 pointer-events-none font-medium" style="top:50%;right:10px;transform:translateY(-50%)">Orchestrating</span>
          <span class="absolute text-[11px] text-gray-400 pointer-events-none font-medium" style="top:6px;left:50%;transform:translateX(-50%)">Critical</span>
          <span class="absolute text-[11px] text-gray-400 pointer-events-none font-medium" style="bottom:22px;left:50%;transform:translateX(-50%)">Niche</span>

          <!-- Dots (one per value_chain entry) -->
          <div
            v-for="dot in filteredDots"
            :key="(dot.actor.elastic_id || dot.actor.name) + '_' + dot.vcEntry.label"
            class="absolute rounded-full cursor-pointer transition-all"
            :style="{
              left: normalizeX(dot.vcEntry.x) + '%',
              top: (100 - normalizeY(dot.vcEntry.y)) + '%',
              width: (dot.vcEntry.size || 8) + 'px',
              height: (dot.vcEntry.size || 8) + 'px',
              backgroundColor: vcColor(dot.vcEntry.label),
              transform: selectedActor && selectedActor.name === dot.actor.name
                ? 'translate(-50%, -50%) scale(1.8)'
                : hoveredDot && hoveredDot.actor.name === dot.actor.name
                  ? 'translate(-50%, -50%) scale(1.6)'
                  : 'translate(-50%, -50%)',
              zIndex: (selectedActor && selectedActor.name === dot.actor.name) || (hoveredDot && hoveredDot.actor.name === dot.actor.name) ? 20 : 1,
              boxShadow: selectedActor && selectedActor.name === dot.actor.name ? '0 0 0 3px ' + vcColor(dot.vcEntry.label) + '40' : 'none',
            }"
            @mouseenter="hoveredDot = dot"
            @mouseleave="hoveredDot = null"
            @click="selectedActor = selectedActor && selectedActor.name === dot.actor.name ? null : dot.actor"
          />

          <!-- Tooltip -->
          <transition name="fade">
            <div
              v-if="hoveredDot && !(selectedActor && selectedActor.name === hoveredDot.actor.name)"
              class="absolute z-30 pointer-events-none bg-gray-900 text-white text-xs rounded-lg px-2.5 py-1.5 whitespace-nowrap shadow-lg"
              :style="tooltipStyle(hoveredDot)"
            >
              {{ hoveredDot.actor.name }}
            </div>
          </transition>
        </div>

        <!-- Selected actor panel (below radar) -->
        <transition name="slide-up">
          <div v-if="selectedActor" class="rounded-xl border border-gray-200 bg-gray-50 p-4 tw-flex gap-4 items-start">
            <div class="w-3 h-3 rounded-full shrink-0 mt-0.5" :style="{ backgroundColor: vcColor(selectedActor.label) }"/>
            <div class="flex-1 min-w-0">
              <div class="tw-flex items-start justify-between gap-2 mb-1">
                <div>
                  <router-link
                    v-if="selectedActor.id"
                    :to="'/actors/' + selectedActor.id"
                    target="_blank"
                    class="no-underline hover:underline"
                  >
                    <h4 class="text-sm font-bold text-primary m-0">{{ selectedActor.name }}</h4>
                  </router-link>
                  <h4 v-else class="text-sm font-bold text-gray-900 m-0">{{ selectedActor.name }}</h4>
                  <p v-if="selectedActor.label" class="text-[11px] text-gray-400 uppercase tracking-wider m-0">{{ selectedActor.label }}</p>
                </div>
                <button @click="selectedActor = null" class="shrink-0 text-gray-400 hover:text-gray-600 transition-colors mt-0.5">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>
              <p v-if="selectedActor.description" class="text-sm text-gray-600 leading-relaxed m-0 mt-2">{{ selectedActor.description }}</p>
            </div>
          </div>
        </transition>

        <!-- Top actors (no actor selected) -->
        <div v-if="!selectedActor">
          <p class="text-[11px] font-semibold uppercase tracking-widest text-gray-400 mb-3">Top actors</p>
          <div class="tw-flex flex-wrap gap-2">
            <button
              v-for="actor in topActors"
              :key="actor.elastic_id || actor.name"
              class="tw-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-50 border border-gray-200 text-xs font-medium text-gray-700 hover:border-gray-300 hover:bg-gray-100 transition-all"
              @click="selectedActor = actor"
            >
              <span class="w-1.5 h-1.5 rounded-full shrink-0"
                    :style="{ backgroundColor: vcColor(actor.vc || (actor.value_chain && actor.value_chain[0] && actor.value_chain[0].label)) }"/>
              {{ actor.name }}
            </button>
          </div>
        </div>

      </div>

    </div>
  </div>
</template>

<script>

  import { fetchInnovationRadar, fetchInnovationRadarQuarters } from '@/api/innovation-radar'

  const VC_COLORS = {
    'Processing & Technology': '#4f6f9f',
    'Ingredients & Intermediates': '#5fbf7f',
    'Input & Feedstock': '#5f7f5f',
    'End Products & Brands': '#2aa7b8',
    'Market Access & Distribution': '#9b59b6',
    'Enabling Infrastructure': '#e07b54',
    'Other': '#9ca3af',
  }

  export default {
    name: 'InnovationRadarDetail',

    data () {
      return {
        isLoading: true,
        radarData: null,
        activeCategories: [],
        hoveredDot: null,
        selectedActor: null,
        radarQuarters: [],
      }
    },

    computed: {
      innovationRadarId () {
        return this.$route.params.id
      },
      year () {
        return Number(this.$route.params.year)
      },
      quarter () {
        return Number(this.$route.params.quarter)
      },
      quarterLabel () {
        if (!this.year || !this.quarter) return ''
        return `Q${this.quarter} ${this.year}`
      },
      actors () {
        return this.radarData?.actors || []
      },
      // Normalize actors to always have a value_chain array (supports legacy {x,y,size,vc} format)
      normalizedActors () {
        return this.actors.map(actor => {
          if (actor.value_chain && actor.value_chain.length) return actor
          return {
            ...actor,
            value_chain: [{ label: actor.label || 'Other', x: actor.x, y: actor.y, size: actor.orchestrationScore * 3 }],
          }
        })
      },
      // Flat list of {actor, vcEntry} — one entry per value_chain position per actor
      filteredDots () {
        const dots = []
        this.normalizedActors.forEach(actor => {
          actor.value_chain.forEach(vcEntry => {
            if (!this.activeCategories.length || this.activeCategories.includes(vcEntry.label)) {
              dots.push({ actor, vcEntry })
            }
          })
        })
        return dots
      },
      valueChainCategories () {
        const map = {}
        this.normalizedActors.forEach(actor => {
          actor.value_chain.forEach(vcEntry => {
            const label = vcEntry.label
            if (!map[label]) map[label] = { vc: label, color: this.vcColor(label), count: 0 }
            map[label].count++
          })
        })
        return Object.values(map)
      },
      topActors () {
        const seen = new Set()
        return this.normalizedActors
          .filter(a => a.value_chain.some(vc => this.activeCategories.includes(vc.label)))
          .sort((a, b) => {
            const maxA = Math.max(...a.value_chain.map(vc => vc.x + vc.y))
            const maxB = Math.max(...b.value_chain.map(vc => vc.x + vc.y))
            return maxB - maxA
          })
          .filter(a => {
            if (seen.has(a.elastic_id || a.name)) return false
            seen.add(a.elastic_id || a.name)
            return true
          })
          .slice(0, 10)
      },
    },

    methods: {
      vcColor (vc) {
        return VC_COLORS[vc] || VC_COLORS['Other']
      },
      async exportPDF () {
        const html2pdf = (await import('html2pdf.js')).default
        await html2pdf().set({
          margin: [12, 14],
          filename: `innovation-radar-${this.quarterLabel.replace(/\s/g, '-').toLowerCase()}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, logging: false },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['css', 'legacy'] },
        }).from(this.buildReportHTML(), 'string').save()
      },
      buildReportHTML () {
        const actors = this.radarData?.actors || []
        const groups = {}
        actors.forEach(a => {
          const vc = a.label || 'Other'
          if (!groups[vc]) groups[vc] = []
          groups[vc].push(a)
        })

        const vcSections = Object.entries(groups).map(([vc, list]) => {
          const color = VC_COLORS[vc] || VC_COLORS['Other']
          const rows = list
            .sort((a, b) => (b.x + b.y) - (a.x + a.y))
            .map(a => `
              <table style="width:100%;table-layout:fixed;border-collapse:collapse;border-bottom:1px solid #f9fafb;">
                <tr>
                  <td style="width:18px;vertical-align:top;padding:5px 8px 5px 0;">
                    <div style="width:8px;height:8px;border-radius:50%;background:${color};margin-top:4px;"></div>
                  </td>
                  <td style="vertical-align:top;padding:5px 0;word-wrap:break-word;overflow-wrap:break-word;">
                    <div style="font-size:13px;font-weight:600;color:#111827;">${a.name}</div>
                    ${a.description ? `<div style="font-size:11px;color:#6b7280;margin-top:2px;line-height:1.5;">${a.description}</div>` : ''}
                  </td>
                </tr>
              </table>`).join('')
          return `
            <div style="margin-bottom:28px;">
              <div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:${color};margin-bottom:10px;">${vc}</div>
              ${rows}
            </div>`
        }).join('')

        const summaryHTML = (this.radarData?.summary || this.radarData?.key_insights) ? `
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:18px;margin-bottom:32px;">
            ${this.radarData?.summary ? `<div style="border:1px solid #e5e7eb;border-radius:8px;padding:14px;">
              <div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;color:#6b7280;margin-bottom:6px;">Summary</div>
              <p style="font-size:13px;color:#374151;line-height:1.7;">${this.radarData.summary}</p>
            </div>` : ''}
            ${this.radarData?.key_insights ? `<div style="border:1px solid #e5e7eb;border-radius:8px;padding:14px;">
              <div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;color:#6b7280;margin-bottom:6px;">Key Insights</div>
              <p style="font-size:13px;color:#374151;line-height:1.7;">${this.radarData.key_insights}</p>
            </div>` : ''}
          </div>` : ''

        return `<div style="width:780px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;color:#1f2937;line-height:1.6;background:#fff;padding:32px 28px 60px;">
          <div style="display:flex;justify-content:space-between;align-items:baseline;border-bottom:2px solid #1f2937;padding-bottom:12px;margin-bottom:32px;">
            <div style="font-size:22px;font-weight:700;">Radar</div>
            <div style="font-size:12px;color:#6b7280;text-transform:uppercase;letter-spacing:0.08em;">${this.quarterLabel}</div>
          </div>
          ${summaryHTML}
          <div style="font-size:14px;font-weight:700;margin-bottom:20px;border-bottom:1px solid #e5e7eb;padding-bottom:8px;">Actors by value chain (${actors.length} total)</div>
          ${vcSections}
        </div>`
      },
      normalizeX (val) {
        // Support both 0-100 and 0-1000 ranges
        return val > 100 ? val / 10 : val
      },
      normalizeY (val) {
        return val > 100 ? val / 10 : val
      },
      toggleCategory (vc) {
        const idx = this.activeCategories.indexOf(vc)
        if (idx === -1) {
          this.activeCategories.push(vc)
        } else {
          this.activeCategories.splice(idx, 1)
        }
      },
      tooltipStyle (dot) {
        const x = this.normalizeX(dot.vcEntry.x)
        const y = 100 - this.normalizeY(dot.vcEntry.y)
        // Flip tooltip to avoid overflowing edges
        const flipX = x > 70 ? 'right: calc(' + (100 - x) + '% + 10px)' : 'left: calc(' + x + '% + 10px)'
        const flipY = y > 70 ? 'bottom: calc(' + (100 - y) + '% + 4px)' : 'top: calc(' + y + '% + 4px)'
        return flipX + '; ' + flipY
      },
      async loadRadarQuarters () {
        try {
          this.radarQuarters = await fetchInnovationRadarQuarters()
        } catch (e) { /* silently ignore */
        }
      },
      async loadRadar () {
        this.isLoading = true
        this.selectedActor = null
        try {
          this.radarData = await fetchInnovationRadar(this.innovationRadarId)
          console.log(this.radarData)
          this.activeCategories = this.valueChainCategories.map(c => c.vc)
        } catch (e) {
          this.radarData = null
        } finally {
          this.isLoading = false
        }
      },
    },

    watch: {
      '$route.params': {
        handler () {
          this.loadRadar()
        },
        immediate: false,
      },
    },

    async mounted () {
      await this.loadRadar()
      this.loadRadarQuarters()
    },
  }
</script>

<style scoped>
  .fade-enter-active, .fade-leave-active {
    transition: opacity 0.15s ease;
  }

  .fade-enter-from, .fade-leave-to {
    opacity: 0;
  }

  .slide-up-enter-active, .slide-up-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
  }

  .slide-up-enter-from, .slide-up-leave-to {
    opacity: 0;
    transform: translateY(6px);
  }
</style>
